/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

// @ts-ignore
/* eslint-disable no-duplicate-imports */
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { IChatContext, TContextProvider } from '@msdyn365-commerce-modules/chat';
import { IPvaChatConnectorViewProps } from '../../pva-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * CartNumberOfItemsTrigger class - proactive chat is triggered if Cart Number of Items is in preset boundaries (config.proactiveOnCartNoOfItemsMin, config.proactiveOnCartNoOfItemsMax)
 */
class CartNumberOfItemsTrigger extends ProactiveChatTrigger {
    private min?: number;
    private max?: number;
    private numberOfItems: number = 0;

    constructor(props: IPvaChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        this.min = this.props.config.proactiveOnCartNoOfItemsMin;
        this.max = this.props.config.proactiveOnCartNoOfItemsMax;

        this.props.context.telemetry.debug(
            `[${CartNumberOfItemsTrigger.name}] Cart number of items proactive chat initialized with min: '${this.min}'; max: '${this.max}'.`
        );
    }

    public async validate(): Promise<boolean> {
        const cart = await this._getCart();
        if (cart.isEmpty) {
            this.props.context.telemetry.debug(`[Chat][${CartNumberOfItemsTrigger.name}] Cart was empty. Validation returning 'false'.`);
            return false;
        }

        this.numberOfItems = cart.cart.TotalItems || 0;

        let retVal = false;
        if (this.min) {
            if (this.max) {
                retVal = this.min <= this.numberOfItems && this.numberOfItems <= this.max;
            } else {
                retVal = this.min <= this.numberOfItems;
            }
        } else {
            if (this.max) {
                retVal = this.numberOfItems <= this.max;
            } else {
                retVal = false;
            }
        }
        this.props.context.telemetry.debug(
            `[${CartNumberOfItemsTrigger.name}] Validation result: '${retVal}'; Number of items in cart: '${this.numberOfItems}'.`
        );
        return retVal;
    }

    public getContextProvider(): TContextProvider {
        return () => {
            return {
                ...this.context,
                eCommerceCustIsProactiveChat: 'True',
                eCommerceCustProactiveType: 'Cart Number of Items',
                eCommerceCustProactiveShoppingCartNumberOfItems: `${this.numberOfItems}`
            };
        };
    }

    public getMessage(): string {
        // @TODO: Add message processing if needed
        return this.props.config.proactiveOnCartNoOfItemsMessage || this.props.config.proactiveDefaultMessage;
    }

    private async _getCart(): Promise<ICartState> {
        return getCartState(this.props.context.actionContext);
    }
}

export default CartNumberOfItemsTrigger;
